/**
 * ErrorBoundary.tsx
 * -----------------
 * React 렌더 에러 캐치 → reportClientError + fallback UI.
 *
 * 사용:
 *   <ErrorBoundary><App /></ErrorBoundary>
 *
 * window.error 핸들러는 동기 JS 에러를 잡지만 React 렌더 에러는 못 잡음.
 * 본 boundary 가 그 영역을 cover.
 */

import { Component, ErrorInfo, ReactNode } from "react";
import { reportClientError } from "../utils/clientErrorReporter";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage?: string;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, errorMessage: err.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError({
      kind: "react",
      message: error.message || "React render error",
      stack: error.stack,
      error_class: error.name || "ReactError",
      // info.componentStack 도 첨부 — React 컴포넌트 트리 디버깅에 유용
      source: (info.componentStack || "").slice(0, 500),
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            fontFamily: "system-ui, sans-serif",
            color: "#475569",
          }}
        >
          <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            화면을 표시하는 중 오류가 발생했습니다.
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
            {this.state.errorMessage}
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "6px 16px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: "white",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            새로고침
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
